"use client"

import { useEffect, useState, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import { CheckCircle, XCircle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { useVerifyPaystackPaymentMutation, useVerifyFlutterwavePaymentMutation } from "@/store"
import { useCart } from "@/context/CartContext"
import { toast } from "sonner"

type PaymentStatus = "verifying" | "success" | "failed"

export default function PaymentCallbackScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { clearCart } = useCart()
  const [status, setStatus] = useState<PaymentStatus>("verifying")
  const [message, setMessage] = useState("")

  const [verifyPaystack] = useVerifyPaystackPaymentMutation()
  const [verifyFlutterwave] = useVerifyFlutterwavePaymentMutation()
  const hasVerified = useRef(false)

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasVerified.current) return
    hasVerified.current = true

    const verifyPayment = async () => {
      const provider = searchParams.get("provider")
      const reference = searchParams.get("reference") || searchParams.get("trxref")
      const transactionId = searchParams.get("transaction_id")
      const txRef = searchParams.get("tx_ref")
      const flwStatus = searchParams.get("status")

      try {
        if (provider === "paystack" && reference) {
          const result = await verifyPaystack({ reference }).unwrap()
          
          if (result.success) {
            setStatus("success")
            setMessage("Your payment was successful! Your order is being processed.")
            // Clear cart if we had a pending order
            if (sessionStorage.getItem("pendingOrderCart")) {
              clearCart()
              sessionStorage.removeItem("pendingOrderCart")
            }
            toast.success("Payment successful!")
          } else {
            setStatus("failed")
            setMessage(result.message || "Payment verification failed. Please contact support.")
          }
        } else if (provider === "flutterwave") {
          // Check if payment was cancelled
          if (flwStatus === "cancelled") {
            setStatus("failed")
            setMessage("Payment was cancelled. You can try again from your cart.")
            return
          }

          // Flutterwave redirects with status, tx_ref, and transaction_id (when successful)
          // We can verify using either transaction_id (preferred) or tx_ref (fallback)
          if (transactionId || txRef) {
            const result = await verifyFlutterwave({ 
              transaction_id: transactionId || undefined,
              tx_ref: txRef || undefined,
            }).unwrap()
            
            if (result.success) {
              setStatus("success")
              setMessage("Your payment was successful! Your order is being processed.")
              // Clear cart if we had a pending order
              if (sessionStorage.getItem("pendingOrderCart")) {
                clearCart()
                sessionStorage.removeItem("pendingOrderCart")
              }
              toast.success("Payment successful!")
            } else {
              setStatus("failed")
              setMessage(result.message || "Payment verification failed. Please contact support.")
            }
          } else {
            setStatus("failed")
            setMessage("Missing transaction information. Please contact support.")
          }
        } else {
          setStatus("failed")
          setMessage("Invalid payment callback. Please contact support.")
        }
      } catch (error: any) {
        console.error("Payment verification error:", error)
        setStatus("failed")
        setMessage(error.data?.error || error.message || "Payment verification failed. Please contact support.")
      }
    }

    verifyPayment()
  }, [searchParams, verifyPaystack, verifyFlutterwave, clearCart])

  return (
    <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-[60vh]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="text-center">
          <CardHeader>
            {status === "verifying" && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-16 w-16 text-primary animate-spin" />
                </div>
                <CardTitle>Verifying Payment</CardTitle>
                <CardDescription>Please wait while we confirm your payment...</CardDescription>
              </>
            )}
            {status === "success" && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <CardTitle className="text-green-600">Payment Successful!</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
            {status === "failed" && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="h-16 w-16 text-red-500" />
                </div>
                <CardTitle className="text-red-600">Payment Failed</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {status === "verifying" && (
              <p className="text-sm text-muted-foreground">
                This may take a few seconds. Please do not close this page.
              </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            {status === "success" && (
              <>
                <Button onClick={() => navigate("/our-menu")} className="w-full">
                  Continue Shopping
                </Button>
                <Button variant="outline" onClick={() => navigate("/orders")} className="w-full">
                  View My Orders
                </Button>
              </>
            )}
            {status === "failed" && (
              <>
                <Button onClick={() => navigate("/cart")} className="w-full">
                  Return to Cart
                </Button>
                <Button variant="outline" onClick={() => navigate("/contact")} className="w-full">
                  Contact Support
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  )
}
